// useLayoutEditorVideoUpload.js

import { useCallback, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import { uploadService } from 'services/nodes/NodeUploadService';

function normalizeString(value) {
  return String(value || '').trim();
}

const VIDEO_UPLOAD_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/*';

/**
 * useLayoutEditorVideoUpload
 *
 * Handles upload-first VIDEO block authoring via the contribution pipeline.
 */
export default function useLayoutEditorVideoUpload({
  onAssignVideoBlock,
  previewNode = null,
  user = null,
} = {}) {
  const inputRef = useRef(null);
  const pendingSlotKeyRef = useRef('');
  const [uploadingSlotKey, setUploadingSlotKey] = useState('');
  const [uploadErrorBySlotKey, setUploadErrorBySlotKey] = useState({});

  const triggerVideoUpload = useCallback((slotKey) => {
    const cleanedSlotKey = normalizeString(slotKey);
    if (!cleanedSlotKey) {
      return;
    }

    pendingSlotKeyRef.current = cleanedSlotKey;
    inputRef.current?.click();
  }, []);

  const handleVideoFileChange = useCallback((event) => {
    const files = Array.from(event?.target?.files || []);
    event.target.value = '';

    const slotKey = normalizeString(pendingSlotKeyRef.current);
    pendingSlotKeyRef.current = '';

    if (!files.length || !slotKey || !previewNode?.key || !user?.key) {
      return;
    }

    setUploadingSlotKey(slotKey);
    setUploadErrorBySlotKey((current) => ({
      ...current,
      [slotKey]: '',
    }));

    const uploadId = uploadService.queueContribution(
      files.map((file) => Object.assign(file, {
        uri: file.uri || URL.createObjectURL(file),
        localUri: file.localUri || file.uri || URL.createObjectURL(file),
      })),
      previewNode,
      user,
      async (uploadedItems) => {
        try {
          const uploadedVideo = (Array.isArray(uploadedItems) ? uploadedItems : [])
            .find((item) => normalizeString(item?.type).toUpperCase() === 'VIDEO')
            || (Array.isArray(uploadedItems) ? uploadedItems[0] : null);

          const nodeKey = normalizeString(uploadedVideo?.key);
          if (!nodeKey) {
            throw new Error('Video upload did not return a node key.');
          }

          await onAssignVideoBlock?.(slotKey, nodeKey);
        } catch (error) {
          console.error('Failed to assign VIDEO block after upload:', error);
          toast.error(error?.message || 'Failed to add video block.');
          setUploadErrorBySlotKey((current) => ({
            ...current,
            [slotKey]: error?.message || 'Failed to add video block.',
          }));
        } finally {
          setUploadingSlotKey('');
        }
      },
      (error) => {
        console.error('Video contribution upload failed:', error);
        toast.error(error?.message || 'Video upload failed.');
        setUploadErrorBySlotKey((current) => ({
          ...current,
          [slotKey]: error?.message || 'Video upload failed.',
        }));
        setUploadingSlotKey('');
      },
    );

    if (uploadId === null) {
      toast.info('A video upload is already in progress for this record.');
      setUploadingSlotKey('');
    }
  }, [onAssignVideoBlock, previewNode, user]);

  return {
    onVideoFileChange: handleVideoFileChange,
    triggerVideoUpload,
    uploadErrorBySlotKey,
    uploadingSlotKey,
    videoUploadAccept: VIDEO_UPLOAD_ACCEPT,
    videoUploadInputRef: inputRef,
  };
}
